/**
 * Numeri del back office che prima stavano solo su Grafana (DEV-382). Solo ciò che porta a un'azione
 * dalla pagina: andamenti e confronti tra aziende restano su Grafana.
 */

export const ADMIN_USAGE_METERS = [
  'AI_EXTRACTION',
  'ASSISTANT',
  'WHATSAPP_AGENT',
  'QR_LABELS',
] as const;
export type AdminUsageMeter = (typeof ADMIN_USAGE_METERS)[number];

export const ADMIN_USAGE_PERIODS = ['DAY', 'WEEK', 'MONTH'] as const;
export type AdminUsagePeriod = (typeof ADMIN_USAGE_PERIODS)[number];

/** Finestra del "picco recente", in periodi del contatore: 30 giorni, 4 settimane, 3 mesi. */
export const ADMIN_USAGE_LOOKBACK_PERIODS: Readonly<Record<AdminUsagePeriod, number>> = {
  DAY: 30,
  WEEK: 4,
  MONTH: 3,
};

export const ADMIN_USAGE_PERIOD_BY_METER: Readonly<Record<AdminUsageMeter, AdminUsagePeriod>> = {
  AI_EXTRACTION: 'WEEK',
  ASSISTANT: 'DAY',
  WHATSAPP_AGENT: 'DAY',
  QR_LABELS: 'MONTH',
};

export interface AdminUsageMeterDto {
  meter: AdminUsageMeter;
  period: AdminUsagePeriod;
  /** Uso del periodo in corso (giorno, settimana o mese, ora di Roma come l'enforcement). */
  currentUsage: number;
  /** Tetto effettivo, risolto con la stessa regola che blocca. */
  limit: number;
  /** Massimo per periodo negli ultimi `ADMIN_USAGE_LOOKBACK_PERIODS[period]` periodi, corrente incluso. */
  peakUsage: number;
  /** Periodi della stessa finestra in cui l'uso ha raggiunto o superato il tetto di oggi. */
  periodsAtLimit: number;
}

export interface AdminTenantUsageDto {
  meters: AdminUsageMeterDto[];
}

export interface AdminDeadlineTypeRowDto {
  /** null = righe senza tipo. */
  documentTypeId: string | null;
  documentTypeName: string | null;
  /** true per il tipo "Altro" (catalogKey `altro`), evidenziato in UI. */
  isOther: boolean;
  active: number;
  recurring: number;
  expired: number;
}

export interface AdminDeadlineCountersDto {
  /** Aperte: non archiviate e non cancellate, scadute comprese. */
  active: number;
  /** Stessa regola dello scadenziario del consulente: una scadenza presa in carico non conta. */
  expired: number;
  /** Con scadenza tra oggi e oggi + 30 giorni, non ancora scadute. */
  dueWithin30Days: number;
  managedByConsultant: number;
  managedByClient: number;
}

export interface AdminTenantDeadlineStatsDto {
  /** Partner nel cui portafoglio sta l'azienda (stessa regola del portafoglio partner); null = nessuno. */
  partner: { id: string; name: string } | null;
  counters: AdminDeadlineCountersDto;
  /** Ordinate per scadenze attive decrescenti. */
  byDocumentType: AdminDeadlineTypeRowDto[];
}

export const ADMIN_PARTNER_DEADLINE_TOP_TENANTS = 15;

export interface AdminPartnerDeadlineTenantRowDto {
  tenantId: string;
  tenantName: string;
  active: number;
  expired: number;
}

export interface AdminPartnerDeadlineCoverageDto {
  portfolioTenants: number;
  /** Aziende del portafoglio con almeno una scadenza attiva. */
  tenantsWithDeadlines: number;
  activeDeadlines: number;
  expiredDeadlines: number;
  /** Solo aziende con scadenze, ordinate per scadute e poi attive decrescenti, al massimo `ADMIN_PARTNER_DEADLINE_TOP_TENANTS`. */
  topTenants: AdminPartnerDeadlineTenantRowDto[];
  /** Aziende con scadenze escluse da `topTenants` ("e altre N"). */
  otherTenantsWithDeadlines: number;
}
